import {
  ResourceCreateDocument,
  ResourceDeleteDocument,
  ResourceDocumentDownloadDocument,
  ResourceDocumentUploadDocument,
  ResourceGetByIdDocument,
  ResourceListDocument,
  ResourceUpdateDocument,
  ResourcesPaginatedDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { IFile } from "api/services/types";
import { Optional, PartialNullable } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  IResourceCreateVars,
  IResourceDocumentModel,
  IResourceListResponse,
  IResourceListVars,
  IResourceModel,
  IResourcePaginatedResponse,
  IResourcePaginatedVars,
  IResourceUpdateVars,
  ResourceCreateUpdateResponseType,
} from "./types";

export async function getResourcesPaginated(
  variables: IResourcePaginatedVars
): Promise<IResourcePaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: ResourcesPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.resourcesPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createResource(
  variables: IResourceCreateVars
): Promise<ResourceCreateUpdateResponseType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ResourceCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    executeEviction(client, {
      typename: "Resource",
      fieldNames: ["resourcesPaginated", "resourceList"],
    });

    return res?.data?.resourceCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateResource(
  variables: IResourceUpdateVars
): Promise<ResourceCreateUpdateResponseType> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ResourceUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    executeEviction(client, {
      typename: "Resource",
      id: variables.resourceId.toString(),
      fieldNames: ["resourcesPaginated", "resourceList", "resourceGetById"],
    });

    return res?.data?.resourceUpdate;
  } catch (error) {
    throw error;
  }
}

export async function getResource(
  resourceId: number
): Promise<PartialNullable<IResourceModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: ResourceGetByIdDocument,
      variables: {
        resourceId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.resourceGetById;
  } catch (error) {
    throw error;
  }
}

export async function deleteResource(resourceId: number): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ResourceDeleteDocument,
      variables: {
        resourceId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "Resource",
      id: resourceId.toString(),
      fieldNames: ["resourcesPaginated", "resourceList"],
    });

    return Boolean(res?.data?.resourceDelete);
  } catch (error) {
    throw error;
  }
}

export async function getResources(
  variables: IResourceListVars
): Promise<IResourceListResponse[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: ResourceListDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.resourceList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function uploadResourceDocument(
  variables: IFile
): Promise<Optional<IResourceDocumentModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ResourceDocumentUploadDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    if (variables.relatedEntityId) {
      executeEviction(client, {
        typename: "Resource",
        id: variables.relatedEntityId.toString(),
        fieldNames: ["resourceGetById", "resourcesPaginated", "resourceList"],
      });
    }

    return res?.data?.resourceDocumentUpload;
  } catch (error) {
    throw error;
  }
}

export async function downloadResourceDocument(
  resourceDocumentId: number
): Promise<Optional<string>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: ResourceDocumentDownloadDocument,
      variables: {
        resourceDocumentId,
      },
      fetchPolicy: "no-cache",
    });

    return res?.data?.resourceDocumentDownload;
  } catch (error) {
    throw error;
  }
}
